import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertCircle, AlertTriangle, Info, ChevronDown, ChevronUp } from 'lucide-react';

const ErrorLog = () => {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, error, warn, info
  const [expandedError, setExpandedError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    fetchErrors();
  }, [filter]);

  const fetchErrors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/system/errors?level=${filter}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setErrors(response.data.errors || []);
    } catch (err) {
      console.error('Error fetching error logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const getLevelIcon = (level) => {
    switch (level?.toLowerCase()) {
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warn':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      default:
        return <Info className="w-5 h-5 text-gray-500" />;
    }
  };

  const getLevelBadge = (level) => {
    const levelLower = level?.toLowerCase();
    const classes = {
      error: 'bg-red-100 text-red-800',
      warn: 'bg-yellow-100 text-yellow-800',
      info: 'bg-blue-100 text-blue-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${classes[levelLower] || 'bg-gray-100 text-gray-800'}`}>
        {level}
      </span>
    );
  };

  const toggleExpand = (errorId) => {
    setExpandedError(expandedError === errorId ? null : errorId);
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-5 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Recent Errors & Logs</h3>

          {/* Filter Buttons */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1 text-sm rounded-md ${
                filter === 'all'
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('error')}
              className={`px-3 py-1 text-sm rounded-md ${
                filter === 'error'
                  ? 'bg-red-100 text-red-700 font-medium'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Errors
            </button>
            <button
              onClick={() => setFilter('warn')}
              className={`px-3 py-1 text-sm rounded-md ${
                filter === 'warn'
                  ? 'bg-yellow-100 text-yellow-700 font-medium'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Warnings
            </button>
          </div>
        </div>
      </div>

      {errors.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-4">
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-sm text-gray-500">No errors found. System is running smoothly!</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-200">
          {errors.map((error, index) => {
            const isExpanded = expandedError === error.id;

            return (
              <div key={error.id || index} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start flex-1 min-w-0">
                    <div className="flex-shrink-0 mt-1">
                      {getLevelIcon(error.level)}
                    </div>
                    <div className="ml-3 flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        {getLevelBadge(error.level)}
                        {error.endpoint && (
                          <span className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                            {error.endpoint}
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          {new Date(error.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-900 break-words">
                        {error.message}
                      </p>

                      {/* Expandable Stack Trace */}
                      {error.stack_trace && (
                        <button
                          onClick={() => toggleExpand(error.id)}
                          className="mt-2 inline-flex items-center text-xs text-blue-600 hover:text-blue-800"
                        >
                          {isExpanded ? (
                            <>
                              <ChevronUp className="w-4 h-4 mr-1" />
                              Hide details
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-4 h-4 mr-1" />
                              Show details
                            </>
                          )}
                        </button>
                      )}

                      {isExpanded && error.stack_trace && (
                        <div className="mt-3 p-3 bg-gray-900 rounded-md overflow-x-auto">
                          <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">
                            {error.stack_trace}
                          </pre>
                        </div>
                      )}

                      {/* Metadata */}
                      {error.metadata && Object.keys(error.metadata).length > 0 && (
                        <div className="mt-2 text-xs text-gray-600">
                          {error.metadata.user_id && (
                            <span className="mr-3">User: {error.metadata.user_id}</span>
                          )}
                          {error.metadata.gym_id && (
                            <span>Gym: {error.metadata.gym_id}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ErrorLog;
